/**
 * MyRequests — "คำขอ & การอนุมัติ ของฉัน" (TASK 25).
 *
 * หน้าเดียว 2 มุม:
 *   - ที่ฉันส่ง  (submitted) — leave + OT + escalation ที่เป็นเจ้าของ
 *   - ที่ฉันอนุมัติ (decided) — l*_approver/resolved_by/reviewed_by = me
 *
 * ทุก role เปิดเมนูเดียวกัน. EMPLOYEE ที่ไม่เคย approve จะเห็น empty state
 * ใน tab "ที่ฉันอนุมัติ" แทน 403.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Send, ClipboardCheck, ClipboardList, Check, X, Ban, HelpCircle,
  AlertTriangle, Clock, Siren, CalendarClock, Pencil, type LucideIcon,
} from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyRequests, useMyDecisions, useCancelRequest, useConsentCancel, type MyRequestItem, type MyDecisionItem, type TimelineStep } from '../hooks';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';

type View = 'submitted' | 'decided';
type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected' | 'info_requested';

// สถานะที่ยังยกเลิกเองได้ (ตรงกับ CANCELLABLE_STATUSES ฝั่ง worker)
const CANCELLABLE = new Set([
  'pending', 'pending_L1', 'pending_L2', 'pending_L3',
  'pending_review', 'info_requested', 'conditionally_approved',
]);

const LEAVE_LABEL: Record<string, string> = {
  sick: 'ลาป่วย',
  personal: 'ลากิจ',
  vacation: 'ลาพักร้อน',
  unpaid: 'ลาไม่รับค่าจ้าง',
  maternity: 'ลาคลอด',
};

const OT_LABEL: Record<string, string> = {
  ot_1: 'OT วันทำงาน (1.5x)',
  ot_2: 'OT วันหยุด (1.0x)',
  ot_3: 'OT เกินเวลาวันหยุด (3.0x)',
};

const ESCALATION_LABEL: Record<string, string> = {
  short_work: 'ทำงานไม่ครบชั่วโมง',
  ot_mismatch: 'OT ไม่ตรงกับคำขอ',
  ot_unrequested: 'OT ที่ไม่ได้ขอ',
  absent_no_evidence: 'ขาดงานไม่มีหลักฐาน',
  over_leave_quota: 'ลาเกินสิทธิ์',
  checkin_geofence_failed: 'สแกนนอกพื้นที่',
  conditional_evidence_overdue: 'หลักฐาน conditional เกินกำหนด',
  late: 'มาสาย',
  unscheduled_work: 'มาทำงานวันที่ไม่ได้กำหนดกะ',
};

function typeLabel(kind: string, type: string): string {
  if (kind === 'leave' || kind === 'emergency_review') return LEAVE_LABEL[type] ?? type;
  if (kind === 'ot') return OT_LABEL[type] ?? type;
  if (kind === 'escalation') return ESCALATION_LABEL[type] ?? type;
  return type;
}

function formatThai(iso: string): string {
  const d = new Date(iso + (iso.length === 10 ? 'T00:00:00+07:00' : ''));
  return new Intl.DateTimeFormat('th-TH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Bangkok',
  }).format(d);
}

function formatTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60_000);
  if (m < 1) return 'เมื่อสักครู่';
  if (m < 60) return `${m} นาทีที่แล้ว`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ชม.ที่แล้ว`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days} วันที่แล้ว`;
  return formatThai(iso.slice(0, 10));
}

const BADGE = 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full';
function StatusBadge({ status, kind }: { status: string; kind?: string }) {
  if (status === 'approved')
    return <span className={`${BADGE} bg-emerald-100 text-emerald-700`}><Check className="w-3 h-3" /> อนุมัติแล้ว</span>;
  if (status === 'rejected')
    return <span className={`${BADGE} bg-red-100 text-red-700`}><X className="w-3 h-3" /> {kind === 'escalation' ? 'ไม่ดำเนินการ' : 'ปฏิเสธ'}</span>;
  if (status === 'cancelled')
    return <span className={`${BADGE} bg-gray-100 text-gray-500`}><Ban className="w-3 h-3" /> ยกเลิกแล้ว</span>;
  if (status === 'info_requested')
    return <span className={`${BADGE} bg-orange-100 text-orange-700`}><HelpCircle className="w-3 h-3" /> ขอข้อมูลเพิ่ม</span>;
  if (status === 'conditionally_approved')
    return <span className={`${BADGE} bg-amber-100 text-amber-700`}><AlertTriangle className="w-3 h-3" /> อนุมัติแบบมีเงื่อนไข</span>;
  if (status === 'pending_review')
    return <span className={`${BADGE} bg-blue-100 text-blue-700`}><Clock className="w-3 h-3" /> HR กำลังพิจารณา</span>;
  // pending / pending_L*
  return <span className={`${BADGE} bg-yellow-100 text-yellow-700`}><Clock className="w-3 h-3" /> รออนุมัติ</span>;
}

function DecisionBadge({ decision }: { decision: MyDecisionItem['decision'] }) {
  const map: Record<MyDecisionItem['decision'], { label: string; cls: string; Icon: LucideIcon }> = {
    approved: { label: 'อนุมัติ', cls: 'bg-emerald-100 text-emerald-700', Icon: Check },
    rejected: { label: 'ปฏิเสธ', cls: 'bg-red-100 text-red-700', Icon: X },
    info_requested: { label: 'ขอข้อมูล', cls: 'bg-orange-100 text-orange-700', Icon: HelpCircle },
    conditionally_approved: { label: 'อนุมัติมีเงื่อนไข', cls: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
    resolved: { label: 'แก้ไขแล้ว', cls: 'bg-emerald-100 text-emerald-700', Icon: Check },
    ignored: { label: 'ไม่ดำเนินการ', cls: 'bg-gray-100 text-gray-700', Icon: Ban },
  };
  const m = map[decision] ?? map.approved;
  const { Icon } = m;
  return <span className={`${BADGE} ${m.cls}`}><Icon className="w-3 h-3" /> {m.label}</span>;
}

function ApprovalTimeline({ timeline }: { timeline: TimelineStep[] }) {
  if (timeline.length === 0) return null;
  const ICON: Record<TimelineStep['decision'], LucideIcon> = {
    approved: Check,
    rejected: X,
    pending: Clock,
    info_requested: HelpCircle,
    conditionally_approved: AlertTriangle,
  };
  return (
    <div className="border-t border-gray-100 mt-2 pt-2 space-y-1">
      {timeline.map((t) => {
        const Icon = ICON[t.decision] ?? Clock;
        return (
        <div key={t.level} className="flex items-center gap-2 text-xs">
          <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
          <span className="font-medium">L{t.level}</span>
          <span className="text-gray-600 truncate">{t.approver_name ?? '—'}</span>
          {t.decided_at && <span className="text-gray-400 ml-auto whitespace-nowrap">{formatTimeAgo(t.decided_at)}</span>}
        </div>
        );
      })}
    </div>
  );
}

function RequestCard({ req }: { req: MyRequestItem }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const cancelReq = useCancelRequest();
  const consentCancel = useConsentCancel();
  if (req.kind === 'escalation') return <EscalationCard esc={req} />;

  // owner/HR ขอยกเลิกใบที่อนุมัติแล้ว รอเรายินยอม
  const onConsent = async (consent: boolean) => {
    const ok = await confirm({
      title: consent ? 'ยินยอมให้ยกเลิก?' : 'ไม่ยินยอมให้ยกเลิก?',
      message: consent
        ? 'การลา/OT ที่อนุมัติไว้จะถูกยกเลิก และคืนสิทธิ์ให้อัตโนมัติ'
        : 'การลา/OT จะยังมีผลตามเดิม และแจ้งกลับผู้ขอว่าคุณไม่ยินยอม',
      confirmText: consent ? 'ยินยอม' : 'ไม่ยินยอม',
      cancelText: 'ย้อนกลับ',
      danger: !consent,
    });
    if (!ok) return;
    try {
      await consentCancel.mutateAsync({ kind: req.kind as 'leave' | 'ot', record_id: req.record_id, consent });
      toast.success(consent ? 'ยินยอมยกเลิกแล้ว' : 'บันทึกว่าไม่ยินยอมแล้ว');
    } catch (e: any) {
      toast.error(e?.message ?? 'ทำรายการไม่สำเร็จ');
    }
  };

  const canCancel = (req.kind === 'leave' || req.kind === 'ot') && CANCELLABLE.has(req.status);
  const onCancel = async () => {
    const isLeave = req.kind === 'leave';
    const ok = await confirm({
      title: isLeave ? 'ยกเลิกคำขอลานี้?' : 'ยกเลิกคำขอ OT นี้?',
      message:
        (req.kind === 'leave' && req.days_count && req.days_count > 1
          ? `จะยกเลิกการลาทั้ง ${req.days_count} วันในคำขอนี้\n`
          : '') + 'หัวหน้าจะไม่เห็นคำขอนี้ให้อนุมัติอีก หากต้องการลา/ทำ OT ภายหลังให้ส่งคำขอใหม่',
      confirmText: 'ยกเลิกคำขอ',
      cancelText: 'ไม่ใช่ตอนนี้',
      danger: true,
    });
    if (!ok) return;
    try {
      await cancelReq.mutateAsync({ kind: req.kind as 'leave' | 'ot', record_id: req.record_id });
      toast.success('ยกเลิกคำขอแล้ว');
    } catch (e: any) {
      toast.error(e?.message ?? 'ยกเลิกไม่สำเร็จ');
    }
  };

  const isMultiDay = req.kind === 'leave' && req.end_date && req.end_date !== req.date;
  return (
    <div className="card p-3">
      <div className="flex justify-between items-start mb-2">
        <div className="min-w-0">
          <StatusBadge status={req.status} />
          <p className="font-medium mt-1">
            {typeLabel(req.kind, req.type)}
            {isMultiDay && ` ${req.days_count ?? 1} วัน`}
            {req.kind === 'ot' && req.start_time && req.end_time && ` ${req.start_time}-${req.end_time}`}
          </p>
          <p className="text-sm text-gray-600">
            {isMultiDay ? `${formatThai(req.date)} – ${formatThai(req.end_date!)}` : formatThai(req.date)}
          </p>
        </div>
        <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap ml-2">{req.id}</span>
      </div>

      {req.is_emergency && (
        <p className="text-xs text-orange-600 mt-1 inline-flex items-center gap-1"><Siren className="w-3 h-3" /> ลาฉุกเฉิน</p>
      )}
      {req.is_backdated && (
        <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> ย้อนหลัง</p>
      )}

      {req.reason && <p className="text-sm text-gray-700 my-1 line-clamp-3">"{req.reason}"</p>}

      {req.status === 'rejected' && req.rejection_reason && (
        <p className="text-sm text-red-700 mt-2 p-2 bg-red-50 rounded">
          เหตุผล: {req.rejection_reason}
        </p>
      )}

      {req.status === 'info_requested' && req.kind === 'leave' && (
        <button
          onClick={() => navigate(`/respond-info/${req.id}`)}
          className="btn-primary w-full mt-2 text-sm inline-flex items-center justify-center gap-1"
        >
          <Pencil className="w-4 h-4" /> ตอบข้อมูลเพิ่ม
        </button>
      )}

      {req.status === 'conditionally_approved' && req.conditional_evidence_due && (
        <p className="text-xs text-amber-700 mt-2 p-2 bg-amber-50 rounded">
          ต้องส่งหลักฐานภายใน {formatThai(req.conditional_evidence_due.slice(0, 10))}
        </p>
      )}

      {req.cancel_requested && (
        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm font-medium text-amber-800 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> บริษัทขอยกเลิกการ{req.kind === 'leave' ? 'ลา' : 'ทำ OT'}นี้</p>
          {req.cancel_reason && <p className="text-sm text-amber-700 mt-1">เหตุผล: {req.cancel_reason}</p>}
          <p className="text-xs text-amber-600 mt-1">ต้องให้คุณยินยอมก่อน ถึงจะยกเลิก</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              onClick={() => onConsent(true)}
              disabled={consentCancel.isPending}
              className="btn-primary justify-center text-sm disabled:opacity-50"
            >
              ยินยอมให้ยกเลิก
            </button>
            <button
              onClick={() => onConsent(false)}
              disabled={consentCancel.isPending}
              className="btn-secondary justify-center text-sm disabled:opacity-50"
            >
              ไม่ยินยอม
            </button>
          </div>
        </div>
      )}

      {req.status !== 'approved' && req.status !== 'cancelled' && req.approval_timeline.length > 0 && (
        <ApprovalTimeline timeline={req.approval_timeline} />
      )}

      {canCancel && (
        <button
          onClick={onCancel}
          disabled={cancelReq.isPending}
          className="btn-secondary w-full mt-2 text-sm text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50"
        >
          {cancelReq.isPending ? 'กำลังยกเลิก…' : 'ยกเลิกคำขอ'}
        </button>
      )}
    </div>
  );
}

function EscalationCard({ esc }: { esc: MyRequestItem }) {
  return (
    <div className="card p-3 border-l-4 border-amber-400">
      <div className="flex justify-between items-start mb-1">
        <StatusBadge status={esc.status} kind="escalation" />
        <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap ml-2">{esc.id}</span>
      </div>
      <p className="font-medium mt-1 inline-flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-amber-500" /> {typeLabel('escalation', esc.type)}</p>
      <p className="text-sm text-gray-600">{formatThai(esc.date)}</p>
      {esc.reason && <p className="text-sm text-gray-700 my-1">{esc.reason}</p>}

      {esc.status === 'approved' && esc.final_decided_at && (
        <p className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1">
          <Check className="w-3 h-3" /> HR ดำเนินการแล้ว ({formatTimeAgo(esc.final_decided_at)})
          {esc.resolution_note ? `: ${esc.resolution_note}` : ''}
        </p>
      )}
      {esc.status === 'rejected' && esc.resolution_note && (
        <p className="text-xs text-gray-700 mt-2">ผู้ตัดสินใจ: {esc.resolution_note}</p>
      )}
    </div>
  );
}

const DECISION_VERB: Record<MyDecisionItem['decision'], string> = {
  approved: 'อนุมัติ',
  rejected: 'ปฏิเสธ',
  info_requested: 'ขอข้อมูลเพิ่ม',
  conditionally_approved: 'อนุมัติแบบมีเงื่อนไข',
  resolved: 'แก้ไข',
  ignored: 'ไม่ดำเนินการ',
};
const ROLE_LABEL: Record<MyDecisionItem['my_role_in_decision'], string> = {
  L1: 'ในฐานะ L1 (หัวหน้างาน)',
  L2: 'ในฐานะ L2 (Manager)',
  L3: 'ในฐานะ L3 (Owner)',
  reviewer: 'ในฐานะ HR review',
  resolver: 'ในฐานะผู้แก้ไข escalation',
};
const KIND_LABEL: Record<MyDecisionItem['kind'], string> = {
  leave: 'ใบลา',
  ot: 'OT',
  emergency_review: 'ลาฉุกเฉิน (review)',
  escalation: 'Escalation',
};

function DecisionCard({ d }: { d: MyDecisionItem }) {
  return (
    <div className="card p-3">
      <div className="flex justify-between items-start mb-2">
        <DecisionBadge decision={d.decision} />
        <span className="text-[10px] text-gray-400 font-mono whitespace-nowrap ml-2">{d.id}</span>
      </div>

      <p className="font-medium">
        {KIND_LABEL[d.kind]}: {typeLabel(d.kind, d.type)}
      </p>
      <p className="text-sm text-gray-600">
        ของ <span className="font-medium">{d.target_emp_name}</span>
        {d.target_department ? ` (${d.target_department})` : ''}
      </p>
      <p className="text-sm text-gray-600">
        {formatThai(d.date)}
        {d.start_time && d.end_time && ` ${d.start_time}-${d.end_time}`}
      </p>

      {d.reason && <p className="text-sm text-gray-700 my-1 line-clamp-3">"{d.reason}"</p>}

      <div className="flex justify-between items-center text-xs text-gray-500 mt-2 pt-2 border-t border-gray-100">
        <span>
          ฉัน{DECISION_VERB[d.decision]} {ROLE_LABEL[d.my_role_in_decision]}
        </span>
        <span className="whitespace-nowrap ml-2">{formatTimeAgo(d.decided_at)}</span>
      </div>

      {d.decision_note && d.decision !== 'approved' && (
        <p className="text-xs text-gray-600 mt-1">หมายเหตุ: {d.decision_note}</p>
      )}

      {/* ถ้าใบยัง not final (เช่นฉัน L1, ต้องไป L2 ต่อ) — แสดง current status */}
      {d.current_level < d.required_levels &&
        d.current_status !== 'rejected' &&
        d.current_status !== 'approved' && (
          <p className="text-xs text-blue-600 mt-1">
            → ส่งต่อระดับ {d.current_level} / {d.required_levels} ({d.current_status})
          </p>
        )}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="text-center text-gray-400 py-12">
      <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

export function MyRequests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = (searchParams.get('view') as View | null) ?? 'submitted';
  const [view, setView] = useState<View>(initialView);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const submitted = useMyRequests({ status: statusFilter });
  const decided = useMyDecisions({ decision: statusFilter === 'all' ? 'all' : statusFilter });

  const onChangeView = (v: View) => {
    setView(v);
    setStatusFilter('all'); // เปลี่ยน view → reset filter
    setSearchParams({ view: v }, { replace: true });
  };

  const isLoading = view === 'submitted' ? submitted.isLoading : decided.isLoading;
  const submittedItems = submitted.data?.items ?? [];
  const decidedItems = decided.data?.items ?? [];

  return (
    <PageLayout title="คำขอ & การอนุมัติของฉัน">
      {/* View tabs */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          onClick={() => onChangeView('submitted')}
          className={`py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 ${
            view === 'submitted' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          <Send className="w-4 h-4" /> ที่ฉันส่ง
          {submitted.data && submitted.data.summary.pending_count > 0 && view !== 'submitted' && (
            <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-[10px]">
              {submitted.data.summary.pending_count}
            </span>
          )}
        </button>
        <button
          onClick={() => onChangeView('decided')}
          className={`py-2 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5 ${
            view === 'decided' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600'
          }`}
        >
          <ClipboardCheck className="w-4 h-4" /> ที่ฉันอนุมัติ
        </button>
      </div>

      {/* Status sub-tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 mb-3">
        {(['all', 'pending', 'approved', 'rejected', 'info_requested'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs whitespace-nowrap ${
              statusFilter === s ? 'bg-navy text-white font-medium' : 'bg-white border border-gray-200 text-gray-600'
            }`}
          >
            {STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      {/* Needs-action banner — เฉพาะ view submitted */}
      {view === 'submitted' && submitted.data && submitted.data.summary.needs_action > 0 && (
        <div className="card p-3 bg-amber-50 border-amber-200 mb-3">
          <p className="text-sm text-amber-800 inline-flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> มี {submitted.data.summary.needs_action} รายการ ต้องตอบกลับ
          </p>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-3">
              <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3 animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && view === 'submitted' && (
        <>
          {submittedItems.length === 0 ? (
            <EmptyState text="ยังไม่มีคำขอในช่วงนี้" />
          ) : (
            <div className="space-y-2">
              {submittedItems.map((r) => (
                <RequestCard key={`${r.kind}-${r.record_id}`} req={r} />
              ))}
            </div>
          )}
        </>
      )}

      {!isLoading && view === 'decided' && (
        <>
          {decidedItems.length === 0 ? (
            <EmptyState text="ยังไม่ได้อนุมัติ / ปฏิเสธ / แก้ไข ให้ใครในช่วงนี้" />
          ) : (
            <div className="space-y-2">
              {decidedItems.map((d) => (
                <DecisionCard key={`${d.kind}-${d.record_id}-${d.decided_at}`} d={d} />
              ))}
            </div>
          )}
        </>
      )}
    </PageLayout>
  );
}

const STATUS_LABEL: Record<StatusFilter, string> = {
  all: 'ทั้งหมด',
  pending: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ปฏิเสธ',
  info_requested: 'ขอข้อมูลเพิ่ม',
};
